import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import Button from '../../components/ui/Button.jsx';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import StatsCards from '../../components/StatsCards.jsx';
import ConfirmDelete from '../../components/ConfirmDelete.jsx';
import Loading from '../../components/Loading.jsx';
import AdminInvitadoForm from './AdminInvitadoForm.jsx';
import { ApiError } from '../../lib/api.js';
import { useInvitados } from '../../hooks/useInvitados.js';
import { useStats } from '../../hooks/useStats.js';
import { confirmadoLabel, formatDate } from '../../lib/format.js';

const TABLE_LIMIT = 80;
const SORT_COLUMNS = {
  nombre: { type: 'text', value: (inv) => inv.nombre },
  email: { type: 'text', value: (inv) => inv.email },
  telefono: { type: 'text', value: (inv) => inv.telefono },
  cupos: { type: 'number', value: (inv) => inv.acompanantes_max },
  confirmados: { type: 'number', value: (inv) => inv.acompanantes_confirmados ?? 0 },
  estado: { type: 'text', value: (inv) => confirmadoLabel(inv.confirmado) },
  cancion: { type: 'text', value: (inv) => inv.cancion_sugerida },
  fecha: { type: 'date', value: (inv) => inv.fecha_confirmacion },
  primerAcceso: { type: 'date', value: (inv) => inv.first_accessed_at },
  ultimoAcceso: { type: 'date', value: (inv) => inv.last_accessed_at },
};

function SortableHeader({ label, sortKey, sort, onSort, className = '' }) {
  const active = sort?.key === sortKey;
  const direction = active ? sort.direction : 'none';
  const indicator = direction === 'ascending' ? '↑' : direction === 'descending' ? '↓' : '↕';

  return (
    <th className={className} aria-sort={active ? direction : undefined}>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        onClick={() => onSort(sortKey)}
      >
        {label}
        <span aria-hidden="true" className="text-text-muted">{indicator}</span>
      </button>
    </th>
  );
}

function comparableValue(value, type) {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return { missing: true, value: null };
  }

  if (type === 'number') {
    const number = Number(value);
    return Number.isFinite(number)
      ? { missing: false, value: number }
      : { missing: true, value: null };
  }

  if (type === 'date') {
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp)
      ? { missing: false, value: timestamp }
      : { missing: true, value: null };
  }

  return { missing: false, value: String(value).trim() };
}

function truncateText(text) {
  if (!text) return '—';
  return text.length > TABLE_LIMIT ? `${text.slice(0, TABLE_LIMIT)}…` : text;
}

async function copyLink(token) {
  const url = `${window.location.origin}/confirmacion/${encodeURIComponent(token)}`;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    // Fall through to textarea fallback below.
  }
  // Fallback for browsers without clipboard API.
  const ta = document.createElement('textarea');
  ta.value = url;
  ta.setAttribute('readonly', '');
  ta.style.position = 'absolute';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
}

// Builds a https://wa.me/<digits>?text=<message> URL so the admin can
// send a pre-filled WhatsApp message to a guest who has a phone on
// file. Strips every non-digit char from the phone — wa.me expects the
// full international number without '+' or spaces.
function whatsappLink(inv) {
  const digits = String(inv.telefono ?? '').replace(/\D+/g, '');
  if (!digits) return null;
  const url = `${window.location.origin}/confirmacion/${encodeURIComponent(inv.token)}`;
  const message =
    `Hola ${inv.nombre} 💍\n\n` +
    `Con mucha ilusión y amor queremos compartir contigo una de las etapas más bonitas de nuestras vidas. 🤍💍\n` +
    `Hemos decidido dar el "sí" para siempre, y nos haría muy felices poder celebrar este día tan especial junto a las personas que queremos. ✨\n` +
    `Te compartimos nuestra invitación con mucho cariño. Esperamos de corazón poder contar contigo en esta ceremonia tan importante para nosotros. 🥂🤍Posterior a la misa, realizaremos una pequeña reunión en casa.\n` +
    `Con amor,\n` +
    `María José & JeanPier 💕\n\n` +
    `${url}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

function ConfirmadoPill({ value }) {
  const label = confirmadoLabel(value);
  const tone =
    value === 1
      ? 'border-success text-success'
      : value === -1
        ? 'border-error text-error'
        : 'border-border text-text-muted';
  return (
    <span
      className={`inline-block rounded border px-2 py-0.5 text-xs ${tone}`}
      data-testid="confirmado-pill"
    >
      {label}
    </span>
  );
}

function TableRowSkeleton() {
  // Mirrors the visible cell count of the real table (Nombre + 8 hidden
  // columns + Acciones = 11). Kept as a fixed-length array rather than
  // threading `colCount` through so the visual weight stays consistent
  // across renders; bump here when columns are added.
  return (
    <tr className="border-b border-border" aria-hidden="true">
      {Array.from({ length: 11 }).map((_, i) => (
        <td key={i} className="px-3 py-3">
          <span className="block h-4 w-20 animate-pulse rounded bg-border" />
        </td>
      ))}
    </tr>
  );
}

export default function AdminInvitados() {
  const { query, createMutation, updateMutation, removeMutation } = useInvitados();
  const statsQuery = useStats();

  const [formState, setFormState] = useState({ open: false, mode: 'create', values: null });
  const [deleteState, setDeleteState] = useState({ open: false, invitado: null });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(null);

  const invitados = query.data ?? [];

  // Client-side name filter. Empty query → show everything. Case-insensitive
  // substring match; trims trailing whitespace from the input so a stray
  // space doesn't empty out the list. No URL sync — kept local to the
  // component per spec.
  const visibleInvitados = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invitados;
    return invitados.filter((inv) => inv.nombre?.toLowerCase().includes(q));
  }, [invitados, search]);

  const sortedInvitados = useMemo(() => {
    if (!sort) return visibleInvitados;

    const column = SORT_COLUMNS[sort.key];
    const direction = sort.direction === 'ascending' ? 1 : -1;
    return visibleInvitados
      .map((inv, index) => ({
        inv,
        index,
        value: comparableValue(column.value(inv), column.type),
      }))
      .sort((left, right) => {
        if (left.value.missing && !right.value.missing) return 1;
        if (!left.value.missing && right.value.missing) return -1;
        if (left.value.missing && right.value.missing) return left.index - right.index;

        const comparison =
          column.type === 'text'
            ? left.value.value.localeCompare(right.value.value, 'es', {
                sensitivity: 'base',
                numeric: true,
              })
            : left.value.value - right.value.value;
        return comparison === 0 ? left.index - right.index : comparison * direction;
      })
      .map(({ inv }) => inv);
  }, [sort, visibleInvitados]);

  function handleSort(sortKey) {
    setSort((current) => ({
      key: sortKey,
      direction:
        current?.key === sortKey && current.direction === 'ascending'
          ? 'descending'
          : 'ascending',
    }));
  }

  function openCreate() {
    setFormState({ open: true, mode: 'create', values: null });
  }

  function openEdit(invitado) {
    setFormState({ open: true, mode: 'edit', values: invitado });
  }

  function closeForm() {
    setFormState({ open: false, mode: 'create', values: null });
  }

  async function handleSubmit(payload) {
    try {
      if (formState.mode === 'edit') {
        await updateMutation.mutateAsync({ id: formState.values.id, ...payload });
        toast.success('Cambios guardados.');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Invitado creado.');
      }
      closeForm();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'No pudimos guardar el invitado. Intentá de nuevo.';
      toast.error(message);
    }
  }

  async function handleCopyLink(invitado) {
    const ok = await copyLink(invitado.token);
    if (ok) {
      toast.success('Link copiado al portapapeles.');
    } else {
      toast.error('No pudimos copiar el link. Copialo manualmente.');
    }
  }

  async function handleConfirmDelete() {
    const target = deleteState.invitado;
    if (!target) return;
    try {
      await removeMutation.mutateAsync(target.id);
      toast.success(`Eliminaste a ${target.nombre}.`);
      setDeleteState({ open: false, invitado: null });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        toast('El invitado ya no existía.', { icon: 'ℹ️' });
        setDeleteState({ open: false, invitado: null });
        return;
      }
      const message =
        err instanceof ApiError ? err.message : 'No pudimos eliminar al invitado. Intentá de nuevo.';
      toast.error(message);
    }
  }

  const isLoading = query.isLoading && invitados.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl text-text">Invitados</h1>
        <Button type="button" onClick={openCreate}>
          Agregar invitado
        </Button>
      </header>

      <StatsCards stats={statsQuery.data} isLoading={statsQuery.isLoading} />

      <div className="max-w-sm">
        <Input
          type="search"
          placeholder="Buscar por nombre…"
          aria-label="Buscar invitado por nombre"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Card className="overflow-x-auto p-0">
        {isLoading ? (
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-border bg-border/30 text-xs uppercase text-text-muted">
              <tr>
                <th className="px-3 py-2">Nombre</th>
                <th className="hidden px-3 py-2 md:table-cell">Email</th>
                <th className="hidden px-3 py-2 md:table-cell">Teléfono</th>
                <th className="hidden px-3 py-2 md:table-cell">Cupos</th>
                <th className="hidden px-3 py-2 md:table-cell">Confirmados</th>
                <th className="px-3 py-2">Estado</th>
                <th className="hidden px-3 py-2 md:table-cell">Canción</th>
                <th className="hidden px-3 py-2 md:table-cell">Fecha</th>
                <th className="hidden px-3 py-2 md:table-cell">Primer acceso</th>
                <th className="hidden px-3 py-2 md:table-cell">Último acceso</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <TableRowSkeleton />
              <TableRowSkeleton />
              <TableRowSkeleton />
            </tbody>
          </table>
        ) : invitados.length === 0 ? (
          !query.isError ? (
            <div className="p-8 text-center text-text-muted">
              <p>Todavía no hay invitados. Agregá el primero.</p>
            </div>
          ) : null
        ) : visibleInvitados.length === 0 ? (
          <div className="p-8 text-center text-text-muted">
            <p>Ningún invitado coincide con “{search.trim()}”.</p>
          </div>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-border bg-border/30 text-xs uppercase text-text-muted">
              <tr>
                <SortableHeader label="Nombre" sortKey="nombre" sort={sort} onSort={handleSort} className="px-3 py-2" />
                <SortableHeader label="Email" sortKey="email" sort={sort} onSort={handleSort} className="hidden px-3 py-2 md:table-cell" />
                <SortableHeader label="Teléfono" sortKey="telefono" sort={sort} onSort={handleSort} className="hidden px-3 py-2 md:table-cell" />
                <SortableHeader label="Cupos" sortKey="cupos" sort={sort} onSort={handleSort} className="hidden px-3 py-2 md:table-cell" />
                <SortableHeader label="Confirmados" sortKey="confirmados" sort={sort} onSort={handleSort} className="hidden px-3 py-2 md:table-cell" />
                <SortableHeader label="Estado" sortKey="estado" sort={sort} onSort={handleSort} className="px-3 py-2" />
                <SortableHeader label="Canción" sortKey="cancion" sort={sort} onSort={handleSort} className="hidden px-3 py-2 md:table-cell" />
                <SortableHeader label="Fecha" sortKey="fecha" sort={sort} onSort={handleSort} className="hidden px-3 py-2 md:table-cell" />
                <SortableHeader label="Primer acceso" sortKey="primerAcceso" sort={sort} onSort={handleSort} className="hidden px-3 py-2 md:table-cell" />
                <SortableHeader label="Último acceso" sortKey="ultimoAcceso" sort={sort} onSort={handleSort} className="hidden px-3 py-2 md:table-cell" />
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sortedInvitados.map((inv) => (
                <tr key={inv.id} className="border-b border-border align-top last:border-b-0">
                  <td className="px-3 py-3 font-medium text-text">{inv.nombre}</td>
                  <td className="hidden px-3 py-3 text-text-muted md:table-cell">{inv.email || '—'}</td>
                  <td className="hidden px-3 py-3 text-text-muted md:table-cell">{inv.telefono || '—'}</td>
                  <td className="hidden px-3 py-3 md:table-cell">{inv.acompanantes_max}</td>
                  <td className="hidden px-3 py-3 md:table-cell">{inv.acompanantes_confirmados ?? 0}</td>
                  <td className="px-3 py-3">
                    <ConfirmadoPill value={inv.confirmado} />
                  </td>
                  <td
                    className="hidden max-w-xs px-3 py-3 text-text-muted md:table-cell"
                    title={inv.cancion_sugerida || ''}
                  >
                    {truncateText(inv.cancion_sugerida)}
                  </td>
                  <td className="hidden px-3 py-3 text-text-muted md:table-cell">
                    {inv.fecha_confirmacion ? formatDate(inv.fecha_confirmacion) : '—'}
                  </td>
                  <td className="hidden px-3 py-3 text-text-muted md:table-cell">
                    {inv.first_accessed_at ? formatDate(inv.first_accessed_at) : '—'}
                  </td>
                  <td className="hidden px-3 py-3 text-text-muted md:table-cell">
                    {inv.last_accessed_at ? formatDate(inv.last_accessed_at) : '—'}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button type="button" size="sm" variant="secondary" onClick={() => openEdit(inv)}>
                        Editar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCopyLink(inv)}
                      >
                        Copiar link
                      </Button>
                      <a
                        href={`${window.location.origin}/confirmacion/${encodeURIComponent(inv.token)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Abrir invitación en otra pestaña"
                        className="inline-flex items-center justify-center rounded-md border border-border bg-bg px-3 py-1 text-sm font-body font-medium text-text transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                      >
                        Abrir
                      </a>
                      {whatsappLink(inv) && (
                        <a
                          href={whatsappLink(inv)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Enviar invitación por WhatsApp a ${inv.telefono}`}
                          className="inline-flex items-center justify-center rounded-md border border-border bg-bg px-3 py-1 text-sm font-body font-medium text-text transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                        >
                          WhatsApp
                        </a>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        onClick={() => setDeleteState({ open: true, invitado: inv })}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <AdminInvitadoForm
        open={formState.open}
        mode={formState.mode}
        defaultValues={formState.values ?? undefined}
        onSubmit={handleSubmit}
        onClose={closeForm}
        isSubmitting={createMutation.isLoading || updateMutation.isLoading}
      />

      <ConfirmDelete
        open={deleteState.open}
        invitado={deleteState.invitado}
        onCancel={() => setDeleteState({ open: false, invitado: null })}
        onConfirm={handleConfirmDelete}
        loading={removeMutation.isLoading}
      />

      {query.isError && (
        <p className="text-sm text-error" role="alert">
          No pudimos cargar los invitados. Refrescá la página para reintentar.
        </p>
      )}

      {isLoading && <Loading label="Cargando invitados..." />}
    </div>
  );
}
